// Канбан (04 §4) — the pipeline as columns.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LOSS_REASONS } from '../leads/useActivity.js';
import {
  useBoardLeads,
  useMoveLead,
  useStages,
  type BoardLead,
  type SalesStage,
} from './useSales.js';
import { IconSearch } from '../../components/Icons.js';
import { useNicheContext, type NicheCity, type NicheVertical } from '../../lib/nicheContext.js';
import './kanban.css';

const VISIBLE_PER_COLUMN = 50;

const TERMINAL_PROMPT: Record<string, string> = {
  won: 'Маркиране като Спечелена сделка. Потвърждавате ли?',
  dnc: 'Внимание: „Не безпокой“ (DNC) е ПОСТОЯННО потискане. Обектът няма да се появява в списъци за обаждания.',
};

function Card({ lead }: { lead: BoardLead }) {
  const tierClass =
    lead.tier === 'A' ? 'badge--tier-a' : lead.tier === 'B' ? 'badge--tier-b' : 'badge--tier-c';

  return (
    <Link
      className="kan__card"
      to={`/leads/${lead.id}`}
      draggable
      onDragStart={(event) => {
        event.dataTransfer.setData('text/lead-id', String(lead.id));
        event.dataTransfer.effectAllowed = 'move';
      }}
    >
      <div className="kan__cardtop">
        <span className="kan__cardname">{lead.business_name}</span>
        {lead.lead_score !== null && (
          <span className="badge badge--score">{lead.lead_score} т.</span>
        )}
      </div>

      <div className="kan__cardbottom">
        <span className="kan__cardmeta">📍 {lead.area ?? lead.city_key}</span>
        {lead.tier && <span className={`badge ${tierClass}`}>Tier {lead.tier}</span>}
      </div>
    </Link>
  );
}

function Column({
  stage,
  leads,
  onDrop,
}: {
  stage: SalesStage;
  leads: BoardLead[];
  onDrop: (leadId: number, stage: SalesStage) => void;
}) {
  const [over, setOver] = useState(false);
  const shown = leads.slice(0, VISIBLE_PER_COLUMN);

  return (
    <section
      className={`kan__col${over ? ' kan__col--over' : ''}`}
      onDragOver={(event) => {
        event.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault();
        setOver(false);
        const id = Number(event.dataTransfer.getData('text/lead-id'));
        if (Number.isFinite(id) && id > 0) onDrop(id, stage);
      }}
    >
      <header className="kan__head" style={{ borderTopColor: stage.color ?? 'var(--rule-strong)' }}>
        <span className="kan__title">{stage.name}</span>
        <span className="badge badge--neutral">{leads.length}</span>
      </header>
      <div className="kan__cards">
        {shown.length === 0 ? (
          <div className="kan__empty">Плъзнете обект тук</div>
        ) : (
          shown.map((lead) => <Card key={lead.id} lead={lead} />)
        )}
        {leads.length > shown.length ? (
          <p className="kan__tail">още {leads.length - shown.length}…</p>
        ) : null}
      </div>
    </section>
  );
}

function LossReasonModal({
  dialog,
  isPending,
  onClose,
  onSubmit,
}: {
  dialog: { leadId: number; stageId: number };
  isPending: boolean;
  onClose: () => void;
  onSubmit: (lossReason: string, lossNotes: string) => void;
}) {
  const [lossReason, setLossReason] = useState<string>(LOSS_REASONS[0]?.value ?? 'no_interest');
  const [lossNotes, setLossNotes] = useState('');

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <form
        className="modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="loss-modal-title"
        data-lead-id={dialog.leadId}
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(lossReason, lossNotes);
        }}
      >
        <div className="modal-card__head">
          <h3 id="loss-modal-title">Причина за загубена сделка</h3>
          <p>Запишете защо разговорът не продължава за точна статистика.</p>
        </div>

        <label className="field">
          <span className="field__label">Причина</span>
          <select
            className="filter-select"
            value={lossReason}
            onChange={(e) => setLossReason(e.target.value)}
          >
            {LOSS_REASONS.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="field__label">Бележка (по желание)</span>
          <input
            type="text"
            className="filter-input"
            value={lossNotes}
            onChange={(e) => setLossNotes(e.target.value)}
            placeholder="Кратък коментар…"
          />
        </label>

        <div className="modal-card__actions">
          <button type="button" className="btn btn--secondary btn--sm" onClick={onClose}>
            Отказ
          </button>
          <button type="submit" className="btn btn--primary btn--sm" disabled={isPending}>
            {isPending ? 'Запис…' : 'Запиши'}
          </button>
        </div>
      </form>
    </div>
  );
}

type TierFilter = 'all' | 'A' | 'B';

function filterBoardLeads(
  leads: BoardLead[],
  tierFilter: TierFilter,
  query: string,
  selectedVertical?: NicheVertical | null,
  selectedCity?: NicheCity | null,
): BoardLead[] {
  const q = query.trim().toLowerCase();
  return leads.filter((lead) => {
    if (
      selectedVertical &&
      (lead as BoardLead & { vertical_key?: string }).vertical_key !== selectedVertical.key
    ) {
      return false;
    }
    if (selectedCity && lead.city_key !== selectedCity.key) {
      return false;
    }
    if (tierFilter !== 'all' && lead.tier !== tierFilter) return false;
    if (q === '') return true;
    return (
      lead.business_name.toLowerCase().includes(q) || (lead.area ?? '').toLowerCase().includes(q)
    );
  });
}

function KanbanToolbar({
  count,
  contextLabel,
  query,
  onQueryChange,
  tierFilter,
  onTierChange,
}: {
  count: number;
  contextLabel: string;
  query: string;
  onQueryChange: (val: string) => void;
  tierFilter: TierFilter;
  onTierChange: (val: TierFilter) => void;
}) {
  return (
    <header className="kan__toolbar">
      <div className="kan__toolbar-left">
        <h1 className="kan__board-title">Търговско табло (Канбан)</h1>
        <span className="badge badge--info">{count} обекта</span>
        <span className="badge badge--neutral kan__niche-badge" title="Активна ниша и територия">
          {contextLabel}
        </span>
        <span className="kan__board-hint">
          Плъзнете карта между колоните, за да смените етапа на сделката
        </span>
      </div>
      <div className="kan__toolbar-right">
        <div className="kan__search">
          <IconSearch size={14} className="kan__search-icon" />
          <input
            type="search"
            placeholder="Филтрирай по име или район…"
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
          />
        </div>
        <div className="tabs-nav">
          {(['all', 'A', 'B'] as const).map((tier) => (
            <button
              key={tier}
              type="button"
              className={`tabs-nav__btn${tierFilter === tier ? ' tabs-nav__btn--active' : ''}`}
              onClick={() => onTierChange(tier)}
            >
              {tier === 'all' ? 'Всички' : `Tier ${tier}`}
            </button>
          ))}
        </div>
      </div>
    </header>
  );
}

export function KanbanPage() {
  const { selectedVertical, selectedCity, label } = useNicheContext();
  const { data: stages = [], isLoading: stagesLoading, error: stagesError } = useStages();
  const { data: leads = [], isLoading: leadsLoading, error: leadsError } = useBoardLeads();
  const move = useMoveLead();
  const [lossDialog, setLossDialog] = useState<{ leadId: number; stageId: number } | null>(null);
  const [query, setQuery] = useState('');
  const [tierFilter, setTierFilter] = useState<TierFilter>('all');

  const contextLabel =
    [selectedCity?.name_bg, selectedVertical?.name_bg].filter(Boolean).join(' · ') ||
    label ||
    'Всички ниши / градове';

  const handleDrop = (leadId: number, stage: SalesStage): void => {
    if (stage.terminal_kind === 'lost') {
      setLossDialog({ leadId, stageId: stage.id });
      return;
    }
    if (stage.is_terminal) {
      const prompt = TERMINAL_PROMPT[stage.terminal_kind ?? ''] ?? 'Преместване в крайна колона.';
      if (!window.confirm(prompt)) return;
    }
    move.mutate({ leadId, stageId: stage.id });
  };

  if (stagesLoading || leadsLoading) return <p className="kan__msg">Зареждане на дъската…</p>;
  const err = stagesError ?? leadsError;
  if (err) {
    return (
      <p className="kan__msg" style={{ color: 'var(--bad)' }}>
        Грешка при зареждане: {err.message}
      </p>
    );
  }
  if (stages.length === 0) {
    return <p className="kan__msg">Няма дефинирани етапи. Добавете ги от Админ.</p>;
  }

  const filteredLeads = filterBoardLeads(leads, tierFilter, query, selectedVertical, selectedCity);
  const unstaged = filteredLeads.filter((lead) => lead.sales_stage_id === null);

  return (
    <div className="kan-container">
      {lossDialog && (
        <LossReasonModal
          dialog={lossDialog}
          isPending={move.isPending}
          onClose={() => setLossDialog(null)}
          onSubmit={(lossReason, lossNotes) =>
            move.mutate(
              {
                leadId: lossDialog.leadId,
                stageId: lossDialog.stageId,
                lossReason,
                lossNotes,
              },
              { onSuccess: () => setLossDialog(null) },
            )
          }
        />
      )}
      <KanbanToolbar
        count={filteredLeads.length}
        contextLabel={contextLabel}
        query={query}
        onQueryChange={setQuery}
        tierFilter={tierFilter}
        onTierChange={setTierFilter}
      />
      <div className="kan">
        {unstaged.length > 0 && (
          <section className="kan__col kan__col--unstaged">
            <header className="kan__head">
              <span className="kan__title">Без етап</span>
              <span className="badge badge--neutral">{unstaged.length}</span>
            </header>
            <div className="kan__cards">
              {unstaged.slice(0, VISIBLE_PER_COLUMN).map((lead) => (
                <Card key={lead.id} lead={lead} />
              ))}
              {unstaged.length > VISIBLE_PER_COLUMN && (
                <p className="kan__tail">още {unstaged.length - VISIBLE_PER_COLUMN}…</p>
              )}
            </div>
          </section>
        )}
        {stages.map((stage) => (
          <Column
            key={stage.id}
            stage={stage}
            leads={filteredLeads.filter((lead) => lead.sales_stage_id === stage.id)}
            onDrop={handleDrop}
          />
        ))}
      </div>
    </div>
  );
}
